<script setup lang="ts">
import { ref, computed, watch, inject, type Ref } from 'vue';
import { useQuery, useMutation } from '@tanstack/vue-query';
import type { AxiosError } from 'axios';
import {
  shiftRecalculationsApi,
  type ShiftAmounts,
  type ShiftRecalculationChange,
  type ShiftRecalculationRow,
} from '@/api/shift-recalculations';
import type { PatientFilter } from '@/api/patients';
import type { SerializedUser } from '@/api/users';
import { formatCurrency } from '@/utils/currency';
import PriceInput from '@/components/price-input.vue';

// Recalculates the amounts of a patient's shifts in a period. Each shift shows
// its amounts now and the ones the pricing in force gives it; editing a row
// makes it "Especial", saved as typed. A shift already charged with a DTE (in a
// charge closure) keeps its price; a salaried one has no pay or tax bill. Only
// the selected rows are applied, all or none.
const props = defineProps<{ patient: PatientFilter; startDate: string; endDate: string }>();

const emit = defineEmits<{ close: []; success: [] }>();

// Only a company issuing tax documents (DTE) has shifts already charged with one.
const currentUser = inject<Ref<SerializedUser>>('currentUser');
const issuesTaxDocuments = computed(() => !!currentUser?.value?.companyIssuesTaxDocuments);

type Mode = 'recalculate' | 'special';
type Edit = { mode: Mode; price: number; cost: number; withTaxBill: boolean };
type ApiError = AxiosError<{ message?: string; errors?: string }>;

const from = ref(props.startDate);
const to = ref(props.endDate);
const caregiverId = ref<number | null>(null);
const selected = ref<number[]>([]);
const edits = ref<Record<number, Edit>>({});
const bulk = ref({ price: 0, cost: 0, withTaxBill: false });
const confirming = ref(false);
const errorMessage = ref('');

const { data: preview, isFetching, refetch, error: previewError } = useQuery<
  Awaited<ReturnType<typeof shiftRecalculationsApi.preview>>, ApiError
>({
  queryKey: computed(() => ['shiftRecalculations', props.patient.id, from.value, to.value]),
  queryFn: () => shiftRecalculationsApi.preview(props.patient.id, from.value, to.value),
  enabled: computed(() => !!from.value && !!to.value),
  retry: false,
});

const perHour = computed(() => preview.value?.perHour ?? false);
const rows = computed(() => preview.value?.shifts ?? []);

const caregivers = computed(() => {
  const byId = new Map(rows.value.map((row) => [row.caregiverId, row.caregiverFullName]));

  return [...byId].map(([value, title]) => ({ value, title }));
});

const visibleRows = computed(() => rows.value.filter(
  (row) => caregiverId.value === null || row.caregiverId === caregiverId.value,
));
const selectedRows = computed(() => visibleRows.value.filter((row) => selected.value.includes(row.id)));

function priceOf(amounts: ShiftAmounts) {
  return (perHour.value ? amounts.pricePerHour : amounts.price) ?? 0;
}

function costOf(amounts: ShiftAmounts) {
  return (perHour.value ? amounts.caregiverCostPerHour : amounts.caregiverCost) ?? 0;
}

function recalculated(row: ShiftRecalculationRow): Edit {
  return {
    mode: 'recalculate',
    price: priceOf(row.recalculated),
    cost: costOf(row.recalculated),
    withTaxBill: !!row.recalculated.withTaxBill,
  };
}

function changesAmounts(row: ShiftRecalculationRow, edit: Edit) {
  return edit.price !== priceOf(row.current) || edit.cost !== costOf(row.current) ||
    edit.withTaxBill !== !!row.current.withTaxBill;
}

// A fresh preview starts every row recalculated, the ones it changes selected.
watch(rows, (loaded) => {
  edits.value = Object.fromEntries(loaded.map((row) => [row.id, recalculated(row)]));
  selected.value = loaded.filter((row) => changesAmounts(row, edits.value[row.id]!)).map((row) => row.id);
  confirming.value = false;
});

function editOf(row: ShiftRecalculationRow): Edit {
  return edits.value[row.id] ?? recalculated(row);
}

function editRow(row: ShiftRecalculationRow, patch: Partial<Edit>) {
  edits.value[row.id] = { ...editOf(row), ...patch, mode: 'special' };
  if (!selected.value.includes(row.id)) selected.value.push(row.id);
}

function resetRow(row: ShiftRecalculationRow) {
  edits.value[row.id] = recalculated(row);
}

function fillSelected() {
  selectedRows.value.forEach((row) => {
    edits.value[row.id] = {
      mode: 'special',
      price: row.chargeClosed ? priceOf(row.current) : bulk.value.price,
      cost: row.salaried ? 0 : bulk.value.cost,
      withTaxBill: row.salaried ? false : bulk.value.withTaxBill,
    };
  });
}

// What a shift is charged or paid in total: per hour, the rate by its hours.
function total(row: ShiftRecalculationRow, amount: number) {
  return perHour.value ? Math.trunc(row.hours * amount) : amount;
}

// A shift in a charge closure keeps its price, even one with no hourly rate.
const priceDifference = computed(() => selectedRows.value.reduce(
  (sum, row) => (row.chargeClosed ? sum : sum + total(row, editOf(row).price) - (row.current.price ?? 0)), 0,
));
const costDifference = computed(() => selectedRows.value.reduce(
  (sum, row) => sum + total(row, editOf(row).cost) - (row.current.caregiverCost ?? 0), 0,
));

function signed(amount: number) {
  return amount > 0 ? `+${formatCurrency(amount)}` : formatCurrency(amount);
}

function changeFor(row: ShiftRecalculationRow): ShiftRecalculationChange {
  const edit = editOf(row);
  const base = { id: row.id, updatedAt: row.updatedAt };
  if (edit.mode === 'recalculate') return { ...base, mode: 'recalculate' };

  const amounts = perHour.value ?
    { pricePerHour: edit.price, caregiverCostPerHour: edit.cost } :
    { price: edit.price, caregiverCost: edit.cost };

  return { ...base, mode: 'special', ...amounts, withTaxBill: edit.withTaxBill };
}

const { mutate: apply, isPending } = useMutation<{ updated: number }, ApiError>({
  mutationFn: () => shiftRecalculationsApi.apply(props.patient.id, selectedRows.value.map(changeFor)),
  onSuccess: () => {
    emit('success');
    emit('close');
  },
  onError: ({ response }) => {
    const data = response?.data ?? {};
    confirming.value = false;
    errorMessage.value = data.errors || 'No se pudieron recalcular los turnos. Intenta nuevamente.';
    // Someone changed a shift meanwhile: the preview is reloaded to review again.
    if (data.message === 'stale_selection') refetch();
  },
});

function askConfirmation() {
  errorMessage.value = '';
  confirming.value = true;
}

const previewErrorMessage = computed(() => previewError.value && (
  previewError.value.response?.data?.errors || 'No se pudieron cargar los turnos.'
));

const costLabel = computed(() => (
  perHour.value ? 'Pago a profesional por hora (líquido)' : 'Pago a profesional (líquido)'
));

const headers = computed(() => [
  { title: 'Fecha', key: 'date', sortable: false },
  { title: 'Profesional', key: 'caregiverFullName' },
  { title: perHour.value ? 'Precio por hora' : 'Precio', key: 'price', sortable: false },
  { title: costLabel.value, key: 'cost', sortable: false },
  { title: 'Con Boleta de Honorarios', key: 'withTaxBill', sortable: false },
  { title: 'Modo', key: 'mode', sortable: false },
]);
</script>

<template>
  <v-card>
    <v-card-title class="d-flex align-center">
      Recalcular turnos de {{ props.patient.fullName }}
      <v-spacer />
      <v-btn
        icon="mdi-close"
        variant="text"
        aria-label="Cerrar"
        @click="emit('close')"
      />
    </v-card-title>
    <v-card-text>
      <p class="mb-4 text-body-2">
        Cada turno toma por defecto los valores vigentes (tarifas del profesional y del paciente,
        con los recargos de feriado). Si editas un valor, el turno pasa a monto especial y se
        guarda tal cual.
        <template v-if="issuesTaxDocuments">
          Los turnos ya cobrados con un DTE mantienen su precio.
        </template>
        Los turnos de un trabajador contratado se pagan con su sueldo: no tienen pago por turno ni
        boleta.
      </p>
      <v-row dense>
        <v-col
          cols="12"
          sm="4"
        >
          <v-text-field
            v-model="from"
            type="date"
            label="Desde"
            variant="outlined"
            density="compact"
          />
        </v-col>
        <v-col
          cols="12"
          sm="4"
        >
          <v-text-field
            v-model="to"
            type="date"
            label="Hasta"
            variant="outlined"
            density="compact"
          />
        </v-col>
        <v-col
          cols="12"
          sm="4"
        >
          <v-select
            v-model="caregiverId"
            :items="caregivers"
            label="Profesional"
            variant="outlined"
            density="compact"
            clearable
          />
        </v-col>
      </v-row>
      <v-alert
        v-if="previewErrorMessage"
        type="error"
        variant="tonal"
        density="compact"
        class="mb-4"
      >
        {{ previewErrorMessage }}
      </v-alert>
      <v-data-table
        v-model="selected"
        :headers="headers"
        :items="visibleRows"
        :loading="isFetching"
        item-value="id"
        show-select
        density="compact"
        loading-text="Cargando turnos..."
        no-data-text="No hay turnos en el período"
      >
        <template #[`item.date`]="{ item }">
          <div class="text-no-wrap">
            {{ item.humanDate }}
          </div>
          <div class="text-caption text-medium-emphasis text-no-wrap">
            {{ item.humanTime }}
          </div>
        </template>
        <template #[`item.price`]="{ item }">
          <div class="py-1 amount-cell">
            <price-input
              :model-value="editOf(item).price"
              :label="perHour ? 'Nuevo precio por hora' : 'Nuevo precio'"
              :hint="`Actual: ${formatCurrency(priceOf(item.current))}`"
              density="compact"
              :disabled="item.chargeClosed"
              @update:model-value="editRow(item, { price: $event })"
            />
            <v-chip
              v-if="item.chargeClosed"
              size="x-small"
              color="grey-darken-1"
              class="mt-1"
            >
              Ya cobrado con DTE
            </v-chip>
          </div>
        </template>
        <template #[`item.cost`]="{ item }">
          <div class="py-1 amount-cell">
            <v-chip
              v-if="item.salaried"
              size="small"
              color="info"
              variant="tonal"
            >
              Trabajador contratado
            </v-chip>
            <price-input
              v-else
              :model-value="editOf(item).cost"
              :label="perHour ? 'Nuevo pago por hora' : 'Nuevo pago'"
              :hint="`Actual: ${formatCurrency(costOf(item.current))}`"
              density="compact"
              @update:model-value="editRow(item, { cost: $event })"
            />
          </div>
        </template>
        <template #[`item.withTaxBill`]="{ item }">
          <v-checkbox-btn
            :model-value="editOf(item).withTaxBill"
            :disabled="item.salaried"
            :aria-label="`Con Boleta de Honorarios, turno ${item.humanDate}`"
            @update:model-value="editRow(item, { withTaxBill: !!$event })"
          />
          <div class="text-caption text-medium-emphasis">
            Actual: {{ item.current.withTaxBill ? 'Sí' : 'No' }}
          </div>
        </template>
        <template #[`item.mode`]="{ item }">
          <v-chip
            :color="editOf(item).mode === 'special' ? 'warning' : 'primary'"
            size="small"
          >
            {{ editOf(item).mode === 'special' ? 'Especial' : 'Recalcular' }}
          </v-chip>
          <v-btn
            v-if="editOf(item).mode === 'special'"
            variant="text"
            size="x-small"
            class="mt-1"
            @click="resetRow(item)"
          >
            Volver a recalcular
          </v-btn>
        </template>
      </v-data-table>
      <v-expansion-panels class="mt-4">
        <v-expansion-panel title="Fijar monto especial en los turnos seleccionados">
          <v-expansion-panel-text>
            <v-row dense>
              <v-col
                cols="12"
                sm="4"
              >
                <price-input
                  v-model="bulk.price"
                  :label="perHour ? 'Precio por hora' : 'Precio'"
                  density="compact"
                  :hint="issuesTaxDocuments ? 'No cambia los turnos ya cobrados con un DTE' : undefined"
                />
              </v-col>
              <v-col
                cols="12"
                sm="4"
              >
                <price-input
                  v-model="bulk.cost"
                  :label="costLabel"
                  density="compact"
                  hint="No cambia el pago de los trabajadores contratados"
                />
              </v-col>
              <v-col
                cols="12"
                sm="4"
              >
                <v-checkbox
                  v-model="bulk.withTaxBill"
                  label="Con Boleta de Honorarios"
                  density="compact"
                />
              </v-col>
            </v-row>
            <v-btn
              color="primary"
              variant="outlined"
              :disabled="selectedRows.length === 0"
              @click="fillSelected"
            >
              Aplicar a {{ selectedRows.length }} seleccionados
            </v-btn>
          </v-expansion-panel-text>
        </v-expansion-panel>
      </v-expansion-panels>
      <p
        class="mt-4 text-body-2"
        aria-live="polite"
      >
        {{ selectedRows.length }} turnos seleccionados · Cobro: {{ signed(priceDifference) }}
        · Pago a profesional: {{ signed(costDifference) }}
      </p>
      <v-alert
        v-if="errorMessage"
        type="error"
        variant="tonal"
        density="compact"
        class="mt-2"
      >
        {{ errorMessage }}
      </v-alert>
    </v-card-text>
    <v-card-actions>
      <template v-if="!confirming">
        <v-btn
          text="Cancelar"
          @click="emit('close')"
        />
        <v-spacer />
        <v-btn
          color="primary"
          variant="flat"
          :disabled="selectedRows.length === 0 || isFetching"
          @click="askConfirmation"
        >
          Recalcular {{ selectedRows.length }} turnos
        </v-btn>
      </template>
      <template v-else>
        <span class="text-body-2 px-2">
          Se actualizarán {{ selectedRows.length }} turnos. ¿Confirmas?
        </span>
        <v-spacer />
        <v-btn
          text="Volver"
          :disabled="isPending"
          @click="confirming = false"
        />
        <v-btn
          color="primary"
          variant="flat"
          text="Confirmar"
          :loading="isPending"
          @click="apply()"
        />
      </template>
    </v-card-actions>
  </v-card>
</template>

<style scoped>
.amount-cell {
  min-width: 150px;
}
</style>
